import React, { useCallback, useState } from "react";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import AiPresetCards from "../../shared/Popup/AiPresetCards";
import { presetTip } from "../../shared/Popup/presetTip";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { compositeCutoutOnWhite } from "../../../lib/compositeCutoutOnWhite";

const MODES = [
  {
    id: "auto",
    label: "Auto",
    sub: "Recommended",
    tip: presetTip('Auto — "Recommended"', [
      "Detects the main subject and removes the background",
      "Best for product shots and portraits",
    ]),
  },
  {
    id: "fine",
    label: "Fine edges",
    sub: "Hair & detail",
    tip: presetTip('Fine edges — "Hair & detail"', [
      "More careful edge handling",
      "Better for hair, fur, and soft boundaries",
    ]),
  },
];

const OUTPUTS = [
  {
    id: "transparent",
    label: "Transparent",
    sub: "PNG cutout",
    tip: presetTip('Transparent — "PNG cutout"', [
      "Keeps a clear background for compositing",
    ]),
  },
  {
    id: "white",
    label: "White",
    sub: "Studio",
    tip: presetTip('White — "Studio"', [
      "Places the subject on a clean white backdrop",
    ]),
  },
];

/**
 * AI Tools → AI Background Remover prep.
 * Auto → quality standard, Fine edges → quality fine.
 * White is a local composite; the stored creation stays the transparent PNG.
 */
const BackgroundRemoverToolPrep = (props) => {
  const [modeId, setModeId] = useState("auto");
  const [outputId, setOutputId] = useState("transparent");

  const handleGenerate = useCallback(
    async ({ token, file, image_url, sourceImageUrl }) => {
      const quality = modeId === "fine" ? "fine" : "standard";
      const { cutoutUrl, creationId } = await ensureBackgroundRemoved({
        token,
        imageFile: file || null,
        imageUrl: file ? null : image_url || sourceImageUrl,
        quality,
      });
      const outputUrl =
        outputId === "white" ? await compositeCutoutOnWhite(cutoutUrl) : cutoutUrl;
      return {
        output_url: outputUrl,
        creation_id: creationId,
      };
    },
    [modeId, outputId]
  );

  return (
    <AiFeatureToolPrep
      {...props}
      onGenerate={handleGenerate}
      generateFileName="bg-remove-input.png"
      downloadFallbackName="background-removed.png"
      toolKey="ai_background_remover"
      title="AI Background Remover"
      aka="BG Remover"
      bio="Remove the background cleanly and keep a sharp cutout."
      emptyPreviewLabel="Upload an image to preview background removal"
      loadingLabel="Removing background…"
      emptyFooterHint="Upload an image, pick a mode, then generate."
      previewTransparent={outputId === "transparent"}
      renderControls={({ busy, clearResult, leftPanelRef }) => (
        <>
          <div className="aftp-section">
            <p className="aftp-label">Mode</p>
            <AiPresetCards
              presets={MODES}
              selectedId={modeId}
              columns={2}
              ariaLabel="Background remove mode"
              boundaryRef={leftPanelRef}
              disabled={busy}
              onSelect={(id) => {
                setModeId(id);
                clearResult();
              }}
            />
          </div>
          <div className="aftp-section">
            <p className="aftp-label">Output</p>
            <AiPresetCards
              presets={OUTPUTS}
              selectedId={outputId}
              columns={2}
              ariaLabel="Output background"
              boundaryRef={leftPanelRef}
              disabled={busy}
              onSelect={(id) => {
                setOutputId(id);
                clearResult();
              }}
            />
          </div>
        </>
      )}
    />
  );
};

export default BackgroundRemoverToolPrep;
